import { z } from "zod";
/**
 * The prompt library boundary.
 *
 * Prompts are the metered thing, so this is the only place in the product where
 * a customer can spend money by typing. Two consequences show up in the shapes
 * below: nothing is created without the quota being checked first, and a
 * question that cannot measure anything is refused with the reason rather than
 * accepted and billed.
 */
/**
 * A saved question.
 *
 * No country and no language. A question is the same question wherever it is
 * asked — the market belongs to the brand, and a report's subscription is what
 * decides where a question gets captured. Carrying a country here made "best
 * code hosting" in India and in the US two library rows that could never be
 * shared.
 */
export const promptSchema = z.object({
    id: z.string().min(1),
    text: z.string().min(1),
    tags: z.array(z.string()),
});
/**
 * A prompt as the library lists it.
 *
 * The library is the workspace's, not a report's — one question is stored once
 * and any number of brand reports subscribe to it. `reports` is that
 * subscription list, and it is the column that makes the screen worth having:
 * without it a person cannot tell a question three brands are watching from one
 * nobody is.
 *
 * **A shared prompt is still captured once.** `runs` are keyed on the prompt,
 * not on a report, and `report_prompts` exists so every subscribing report can
 * be scored from the same capture — its own index carries the comment "which
 * reports must be scored for this capture". Adding a second brand to a question
 * costs another scoring pass, never another visit to an engine.
 */
export const libraryPromptSchema = promptSchema.extend({
    reports: z.array(z.object({
        reportId: z.string(),
        brandId: z.string(),
        brandName: z.string(),
        /** The report keeps this question's answers but captures nothing new for it. */
        paused: z.boolean(),
    })),
    /** When it entered the library, so the list can be ordered by newest. */
    createdAt: z.string(),
});
/**
 * A saved question, and what was scheduled for it right away.
 *
 * `scheduled` is how many engines will capture it today rather than tomorrow.
 * Zero is a real answer — today may already be covered, or the queue may have
 * refused — and the screen says which rather than implying a capture that is
 * not coming (AGENTS.md §1).
 */
export const createdPromptSchema = promptSchema.extend({
    scheduled: z.number().int().min(0),
});
/**
 * Usage travels with the list, always.
 *
 * A library screen that shows prompts without showing "38 of 50" leaves the
 * customer to discover the limit by hitting it.
 */
export const promptLibrarySchema = z.object({
    prompts: z.array(libraryPromptSchema),
    used: z.number().int().min(0),
    quota: z.number().int().min(0),
});
export const promptProblemSchema = z.object({
    code: z.enum(["names_brand", "contains_link", "too_short", "too_long"]),
    detail: z.string(),
    /** Character offset, when the problem has one. Null for whole-prompt problems. */
    index: z.number().int().min(0).nullable(),
});
export const createPromptSchema = z.object({
    text: z.string().trim().min(1).max(1000),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
});
export const checkPromptSchema = z.object({ text: z.string().max(2000) });
/**
 * Empty `problems` means we found nothing wrong — not that the prompt is good.
 * The validator knows the brands this workspace tracks and nothing else, and
 * saying so is cheaper than a customer discovering it a month into a report.
 */
export const promptCheckSchema = z.object({
    problems: z.array(promptProblemSchema),
});
/**
 * Asking for questions to be deleted.
 *
 * A body on a `DELETE`. Fastify 5.11 parses one — `DELETE` is in its
 * `bodywith` set, not its `bodyless` one — so the verb can say what happened
 * instead of a `POST` to a `/delete` path existing to route around a stack that
 * ignores it. Checked in the installed source rather than recalled.
 *
 * Bounded, so one call is never an unbounded write.
 */
export const deletePromptsSchema = z.object({
    promptIds: z.array(z.string().min(1)).min(1).max(500),
});
/**
 * How many rows were actually removed.
 *
 * Counted, never assumed from the request. A selection whose questions another
 * tab already deleted comes back as a smaller number, which is the honest
 * answer to "did that work" rather than one inferred from what was sent (§1).
 */
export const deletedPromptsSchema = z.object({
    deleted: z.number().int().min(0),
});
//# sourceMappingURL=prompts.js.map